import { apiClient } from "./api-client";

interface AuthUser {
  id: string;
  email: string;
  role: string;
  tenant_id: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
}

export async function login(
  email: string,
  password: string,
): Promise<AuthUser> {
  const response = await apiClient<LoginResponse>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    skipAuth: true,
  });

  localStorage.setItem("access_token", response.access_token);
  localStorage.setItem("refresh_token", response.refresh_token);
  localStorage.setItem("user", JSON.stringify(response.user));

  return response.user;
}

export async function verifyEmail(token: string): Promise<void> {
  await apiClient<unknown>("/auth/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
    skipAuth: true,
  });
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiClient<unknown>("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ email }),
    skipAuth: true,
  });
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<void> {
  await apiClient<unknown>("/auth/confirm-reset", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
    skipAuth: true,
  });
}

export function logout(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
  window.location.href = "/login";
}

export function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

// Deduplicate concurrent refresh attempts (e.g. multiple 401s on page load)
let refreshPromise: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = attemptRefresh();
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

async function attemptRefresh(): Promise<boolean> {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) return false;

  try {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL || ""}/api/v1/auth/refresh`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ refresh_token: refreshToken }),
      },
    );

    if (!response.ok) return false;

    const data = (await response.json()) as {
      data?: { access_token?: string; refresh_token?: string };
      access_token?: string;
      refresh_token?: string;
    };
    const tokens = data.data ?? data;
    if (tokens.access_token) {
      localStorage.setItem("access_token", tokens.access_token);
      if (tokens.refresh_token) {
        localStorage.setItem("refresh_token", tokens.refresh_token);
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem("access_token");
}

export function getCurrentUser(): AuthUser | null {
  const raw = localStorage.getItem("user");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}
